import { useState } from "react";
import { useEditMode } from "@/contexts/EditModeContext";
import { useProductEditor } from "@/hooks/useProductEditor";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Edit2, Save, X } from "lucide-react";

interface EditableBestForProps {
  productId: string;
  initialValue?: string[];
}

const bestForOptions = [
  { id: "new-agents", label: "New Agents" },
  { id: "growing-agents", label: "Growing Agents" },
  { id: "top-producers", label: "Top Producers" },
  { id: "team-leaders-brokers", label: "Team Leaders & Brokers" },
];

export const EditableBestFor = ({ productId, initialValue = [] }: EditableBestForProps) => {
  const { isEditMode } = useEditMode();
  const { updateProduct } = useProductEditor();
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState<string[]>(initialValue);

  if (!isEditMode) {
    if (initialValue.length === 0) return null;
    
    return (
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="font-semibold">Best for:</h3>
        <div className="flex flex-wrap gap-2">
          {initialValue.map((id) => {
            const option = bestForOptions.find(opt => opt.id === id);
            return option ? (
              <span key={id} className="bg-muted px-3 py-1 rounded-full text-sm">
                {option.label}
              </span>
            ) : null;
          })}
        </div>
      </div>
    );
  }

  const handleSave = () => {
    updateProduct({ productId, field: "best_for", value: selected });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setSelected(initialValue);
    setIsEditing(false);
  };

  const toggleSelection = (id: string) => {
    setSelected(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  if (!isEditing) {
    return (
      <div className="border-2 border-dashed border-primary/50 p-4 rounded-lg">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-semibold">Best for:</h3>
          {initialValue.length > 0 ? (
            <div className="flex flex-wrap gap-2 flex-1">
              {initialValue.map((id) => {
                const option = bestForOptions.find(opt => opt.id === id);
                return option ? (
                  <span key={id} className="bg-muted px-3 py-1 rounded-full text-sm">
                    {option.label}
                  </span>
                ) : null;
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No agent types selected</p>
          )}
          <Button size="sm" variant="outline" onClick={() => setIsEditing(true)} className="ml-auto">
            <Edit2 className="h-4 w-4 mr-1" />
            Edit
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="border-2 border-primary p-4 rounded-lg space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Best for:</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handleCancel}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave}>
            <Save className="h-4 w-4 mr-1" />
            Save
          </Button>
        </div>
      </div>
      
      <div className="space-y-3">
        {bestForOptions.map((option) => (
          <div key={option.id} className="flex items-center space-x-2">
            <Checkbox
              id={`edit-${option.id}`}
              checked={selected.includes(option.id)}
              onCheckedChange={() => toggleSelection(option.id)}
            />
            <Label htmlFor={`edit-${option.id}`} className="cursor-pointer">
              {option.label}
            </Label>
          </div>
        ))}
      </div>
    </div>
  );
};
